import { c as _c } from "react/compiler-runtime";
import React from "react";
import { InlineRichText } from "@/components/atoms/RichText";
import type { PortableTextBlock } from "@portabletext/types";
import * as LucideIcons from "lucide-react";
import { ChevronRight } from "lucide-react";
import { SECTION_CONTAINER_WIDTHS, type SectionContainerWidth } from "@/components/atoms/section-constants";
import { SectionBackground } from "../modules/Section/SectionBackground";
import { SectionContent } from "../modules/Section/SectionContent";
import { BackgroundPatternType } from "@/components/atoms/BackgroundPattern";
import { isDarkColor } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { ContentImage, ContentVideo } from "@/content/types";
import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";
interface Step {
  _key?: string;
  title: string;
  content?: PortableTextBlock[];
  icon?: string;
}
interface PortableProcessStepsProps {
  value: {
    heading?: string;
    introduction?: PortableTextBlock[];
    steps: Step[];
    displayStyle?: "vertical" | "horizontal" | "cards";
    style?: {
      backgroundColor?: string;
      backgroundImage?: ContentImage;
      backgroundVideo?: ContentVideo;
      backgroundPattern?: string;
      patternOpacity?: number;
      textColor?: string;
      paddingTop?: string;
      paddingBottom?: string;
      containerWidth?: string;
      customClasses?: string;
    };
  };
}
const paddingMapping: Record<string, string> = {
  "pt-0": "pt-0",
  "pt-8": "pt-8",
  "pt-16": "pt-16",
  "pt-24": "pt-24",
  "pt-32": "pt-32",
  "pb-0": "pb-0",
  "pb-8": "pb-8",
  "pb-16": "pb-16",
  "pb-24": "pb-24",
  "pb-32": "pb-32"
};

/**
 * Get Lucide icon component by name
 */
function getIconComponent(iconName?: string) {
  if (!iconName) return null;
  return (LucideIcons as unknown as Record<string, React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>>)[iconName];
}

/**
 * Vertical Timeline Layout
 * Steps displayed vertically with numbered circles and connecting line
 */
function VerticalTimelineLayout(t0) {
  const $ = _c(11);
  const {
    steps,
    bgColor
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      left: "30px",
      top: "24px",
      bottom: "24px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const connectingLineStyle = t1 as const;
  const t2 = `0 0 0 6px var(--color-aq-${bgColor}, white)`;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      boxShadow: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const stepCircleShadowStyle = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Box className="absolute w-1 rounded-full bg-primary/75" style={connectingLineStyle} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== stepCircleShadowStyle || $[5] !== steps) {
    let t6;
    if ($[7] !== stepCircleShadowStyle) {
      t6 = (step, index) => {
        const IconComponent = getIconComponent(step.icon);
        return <Box key={step._key || `step-${index}`} className="relative flex gap-6 md:gap-8"><Box className={cn("relative z-10 flex-shrink-0", "w-12 h-12 md:w-16 md:h-16", "rounded-full flex items-center justify-center", "bg-primary text-primary-foreground", "font-bold text-lg md:text-xl", "shadow-lg")} style={stepCircleShadowStyle}>{IconComponent ? <IconComponent className="w-6 h-6 md:w-8 md:h-8" strokeWidth={2} /> : index + 1}</Box><Box className="flex-1 pt-1 md:pt-3"><Text as="p" className="text-xl md:text-2xl font-bold mb-3">{step.title}</Text>{step.content && step.content.length > 0 && <Box className="prose prose-lg max-w-none opacity-80"><InlineRichText content={step.content} /></Box>}</Box></Box>;
      };
      $[7] = stepCircleShadowStyle;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    t5 = steps.map(t6);
    $[4] = stepCircleShadowStyle;
    $[5] = steps;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <Box className="relative">{t4}<Box className="space-y-8 md:space-y-12">{t5}</Box></Box>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/**
 * Horizontal Flow Layout
 * Steps displayed horizontally with arrows between them
 */
function HorizontalFlowLayout(t0) {
  const $ = _c(8);
  const {
    steps,
    isDark
  } = t0;
  let t1;
  if ($[0] !== isDark || $[1] !== steps) {
    let t2;
    if ($[3] !== isDark || $[4] !== steps.length) {
      t2 = (step, index) => {
        const IconComponent = getIconComponent(step.icon);
        const isLast = index === steps.length - 1;
        return <React.Fragment key={step._key || `step-${index}`}><Box className={cn("flex flex-col items-center text-center", "w-40 md:w-48 lg:w-56 flex-shrink-0 md:flex-shrink")}><Box className={cn("w-16 h-16 md:w-20 md:h-20 mb-4", "rounded-full flex items-center justify-center", "bg-primary text-primary-foreground", "font-bold text-xl md:text-2xl", "shadow-lg")}>{IconComponent ? <IconComponent className="w-8 h-8 md:w-10 md:h-10" strokeWidth={2} /> : index + 1}</Box><Text as="p" className="text-base md:text-lg font-bold mb-2">{step.title}</Text>{step.content && step.content.length > 0 && <Box className="prose prose-sm max-w-none opacity-70 line-clamp-3"><InlineRichText content={step.content} /></Box>}</Box>{!isLast && <Box className={cn("flex items-center justify-center", "self-start mt-6 md:mt-8", "px-2 md:px-4", isDark ? "text-white/40" : "text-foreground/40")}><ChevronRight className="w-6 h-6 md:w-8 md:h-8" strokeWidth={2} /></Box>}</React.Fragment>;
      };
      $[3] = isDark;
      $[4] = steps.length;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = steps.map(t2);
    $[0] = isDark;
    $[1] = steps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <Box className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0"><Box className="flex flex-nowrap md:flex-wrap justify-start md:justify-center gap-4 md:gap-2 min-w-max md:min-w-0">{t1}</Box></Box>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * Numbered Cards Layout
 * Steps displayed as a grid of numbered cards
 */
function NumberedCardsLayout(t0) {
  const $ = _c(7);
  const {
    steps,
    isDark
  } = t0;
  let t1;
  if ($[0] !== isDark || $[1] !== steps) {
    let t2;
    if ($[3] !== isDark) {
      t2 = (step, index) => {
        const IconComponent = getIconComponent(step.icon);
        return <Box key={step._key || `step-${index}`} className={cn("relative rounded-2xl p-6 md:p-8", isDark ? "bg-white/10 border border-white/20" : "bg-foreground/5 border border-foreground/10", "shadow-lg", "transition-all duration-300", isDark ? "hover:bg-white/15 hover:border-white/30" : "hover:bg-foreground/10 hover:border-foreground/20")}><Box className={cn("absolute -top-3 -left-3", "w-10 h-10 md:w-12 md:h-12", "rounded-full flex items-center justify-center", "bg-primary text-primary-foreground", "font-bold text-lg md:text-xl", "shadow-lg")}>{index + 1}</Box>{IconComponent && <Box className={cn("mb-4 opacity-60", isDark ? "text-white" : "text-foreground")}><IconComponent className="w-8 h-8" strokeWidth={1.5} /></Box>}<Text as="p" className="text-lg md:text-xl font-bold mb-3 pr-4">{step.title}</Text>{step.content && step.content.length > 0 && <Box className="prose prose-sm md:prose-base max-w-none opacity-80"><InlineRichText content={step.content} /></Box>}</Box>;
      };
      $[3] = isDark;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = steps.map(t2);
    $[0] = isDark;
    $[1] = steps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 pt-4">{t1}</Box>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/**
 * PortableProcessSteps Component
 *
 * A block for displaying sequential processes with visual step indicators.
 * Supports 3 display variants: vertical timeline, horizontal flow, and numbered cards.
 */
export function PortableProcessSteps(t0) {
  const $ = _c(34);
  const {
    value
  } = t0;
  const {
    heading,
    introduction,
    steps,
    displayStyle: t1,
    style
  } = value;
  const displayStyle = t1 === undefined ? "vertical" : t1;
  const bgColor = style?.backgroundColor || "off-white";
  const paddingTop = style?.paddingTop || "pt-16";
  const paddingBottom = style?.paddingBottom || "pb-16";
  const containerWidthValue = style?.containerWidth || "max-w-6xl";
  const ptClass = paddingMapping[paddingTop] || "pt-16";
  const pbClass = paddingMapping[paddingBottom] || "pb-16";
  const containerSize = (containerWidthValue in SECTION_CONTAINER_WIDTHS ? containerWidthValue : "max-w-7xl") as SectionContainerWidth;
  let t2;
  if ($[0] !== bgColor) {
    t2 = isDarkColor(bgColor);
    $[0] = bgColor;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isDark = t2;
  if (!steps || steps.length === 0) {
    return null;
  }
  let t3;
  if ($[2] !== bgColor || $[3] !== displayStyle || $[4] !== isDark || $[5] !== steps) {
    t3 = () => {
      switch (displayStyle) {
        case "horizontal":
          {
            return <HorizontalFlowLayout steps={steps} isDark={isDark} />;
          }
        case "cards":
          {
            return <NumberedCardsLayout steps={steps} isDark={isDark} />;
          }
        case "vertical":
        default:
          {
            return <VerticalTimelineLayout steps={steps} bgColor={bgColor} />;
          }
      }
    };
    $[2] = bgColor;
    $[3] = displayStyle;
    $[4] = isDark;
    $[5] = steps;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const renderLayout = t3;
  const t4 = heading || "Process steps";
  const t5 = style?.customClasses;
  let t6;
  if ($[7] !== pbClass || $[8] !== ptClass || $[9] !== t5) {
    t6 = cn("process-steps", "rounded-2xl md:rounded-3xl border border-white/10 shadow-2xl", ptClass, pbClass, t5);
    $[7] = pbClass;
    $[8] = ptClass;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = style?.backgroundImage;
  const t8 = style?.backgroundVideo;
  const t9 = style?.backgroundPattern as BackgroundPatternType;
  const t10 = style?.patternOpacity;
  let t11;
  if ($[11] !== heading) {
    t11 = heading && <Heading level={2} className="text-2xl md:text-3xl font-bold tracking-tight mb-6">{heading}</Heading>;
    $[11] = heading;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== introduction) {
    t12 = introduction && introduction.length > 0 && <Box className="prose prose-lg max-w-none mb-10 opacity-80"><InlineRichText content={introduction} /></Box>;
    $[13] = introduction;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== renderLayout) {
    t13 = renderLayout();
    $[15] = renderLayout;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t11 || $[18] !== t12 || $[19] !== t13) {
    t14 = <Box className="relative z-10">{t11}{t12}{t13}</Box>;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== containerSize || $[22] !== t14) {
    t15 = <SectionContent size={containerSize}>{t14}</SectionContent>;
    $[21] = containerSize;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== bgColor || $[25] !== isDark || $[26] !== t10 || $[27] !== t15 || $[28] !== t4 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t16 = <Box className="not-prose my-16"><SectionBackground as="section" aria-label={t4} className={t6} backgroundColor={bgColor} backgroundImage={t7} backgroundVideo={t8} backgroundPattern={t9} patternOpacity={t10} isDark={isDark}>{t15}</SectionBackground></Box>;
    $[24] = bgColor;
    $[25] = isDark;
    $[26] = t10;
    $[27] = t15;
    $[28] = t4;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
}
export default PortableProcessSteps;